<template>
  <v-dialog max-width="723">
    <template v-slot:activator="{ props: activatorProps }">
      <!-- Settings Button -->
      <v-btn
        v-bind="activatorProps"
        icon
        size="40"
        class="settings-icon"
        aria-label="Open settings"
      >
        <v-icon :size="24" aria-hidden="true">mdi-cog-outline</v-icon>
      </v-btn>
    </template>

    <template v-slot:default="{ isActive }">
      <v-card>
        <div class="settings-title">
          <span>Settings</span>
          <v-btn
            icon
            size="40"
            variant="text"
            aria-label="Close settings dialog"
            @click="isActive.value = false"
          >
            <v-icon :size="24" aria-hidden="true"
              >mdi-close-circle-outline</v-icon
            >
          </v-btn>
        </div>

        <div class="settings-subtitle mt-3">
          Configure the model and API key used for your integration
        </div>

        <!-- Error Message -->
        <v-alert
          v-if="configStore.error"
          type="error"
          closable
          class="mt-6"
          @click:close="configStore.clearError()"
        >
          {{ configStore.error }}
        </v-alert>

        <!-- Loading State -->
        <div v-if="configStore.isLoading" class="text-center mt-8">
          <v-progress-circular indeterminate></v-progress-circular>
        </div>

        <!-- Form Content -->
        <div v-else>
          <v-select
            v-model="selectedModel"
            flat
            hide-details
            bg-color="#f9f9f9"
            icon-color="1E1E1E"
            label="LLM Model"
            variant="solo-filled"
            :items="modelOptions"
            class="mt-8"
            :disabled="configStore.isSaving"
          ></v-select>

          <v-text-field
            v-model="apiKey"
            flat
            hide-details
            bg-color="#f9f9f9"
            label="API Key"
            type="password"
            variant="solo-filled"
            class="mt-4"
            :disabled="configStore.isSaving"
          ></v-text-field>

          <div class="mt-12 action-buttons">
            <v-btn
              v-if="configStore.config"
              variant="outlined"
              class="delete-btn"
              @click="handleDelete()"
              :loading="configStore.isSaving"
            >
              Delete Configuration
            </v-btn>
            <v-btn
              variant="tonal"
              class="save-btn"
              @click="handleSave()"
              :loading="configStore.isSaving"
              :disabled="!canSave"
            >
              {{
                configStore.config
                  ? 'Update Configuration'
                  : 'Save Configuration'
              }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </template>
  </v-dialog>
</template>

<script setup lang="ts">
import type { AllowedModel } from '@/services/merchantConfigService';
import { useConfigStore } from '@/stores/configStore';
import { computed, onMounted, ref } from 'vue';

const configStore = useConfigStore();

const selectedModel = ref<AllowedModel>('gpt-4o-mini');
const apiKey = ref('');

const modelOptions = [
  { title: 'GPT-4o Mini', value: 'gpt-4o-mini' },
  { title: 'GPT-4o', value: 'gpt-4o' },
  { title: 'GPT-4.1 Mini', value: 'gpt-4.1-mini' },
  { title: 'GPT-4.1 Nano', value: 'gpt-4.1-nano' },
  { title: 'GPT-4.1', value: 'gpt-4.1' },
];

const canSave = computed(() => {
  return selectedModel.value && apiKey.value.trim() && !configStore.isSaving;
});

const handleSave = async () => {
  try {
    if (configStore.config) {
      await configStore.updateConfig({
        model: selectedModel.value,
        llm_api_key: apiKey.value,
      });
    } else {
      await configStore.createConfig({
        model: selectedModel.value,
        llm_api_key: apiKey.value,
      });
    }
  } catch (error) {
    console.error('Failed to save configuration:', error);
  }
};

const handleDelete = async () => {
  if (
    confirm(
      'Are you sure you want to delete this configuration? This action cannot be undone.',
    )
  ) {
    try {
      await configStore.deleteConfig();
      // Reset form
      selectedModel.value = 'gpt-4o-mini';
      apiKey.value = '';
    } catch (error) {
      console.error('Failed to delete configuration:', error);
    }
  }
};

onMounted(async () => {
  await configStore.loadConfig();
  if (configStore.config) {
    selectedModel.value = configStore.config.model;
    // API key is not returned from the server for security reasons
    apiKey.value = '';
  }
});
</script>

<style scoped lang="scss">
.settings-icon {
  background-color: rgb(var(--v-theme-background));
  border-radius: 8px;
}

.v-card {
  padding: 32px;
  border-radius: 24px !important;
  background-color: #fff;
  color: black;
}

.settings-title {
  display: flex;
  align-self: center;
  justify-content: space-between;
  width: 100%;

  span {
    color: #303030;
    font-family: Poppins;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  .v-icon {
    cursor: pointer;
  }
}

.settings-subtitle {
  color: #303030;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 30px */
}

.v-btn-toggle {
  min-height: 44px;
  padding: 4px;
  border-radius: 9px;
  background-color: #f3f3f3;

  .v-btn {
    flex: 1;
    padding: 8px 32px;
    border-radius: 8px !important;
    background-color: transparent;

    color: #1e1e1e;
    text-align: center;
    text-overflow: ellipsis;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px; /* 100% */

    transition: all 0.3s ease;
  }

  .v-btn--active {
    padding: 8px 40px;
    background-color: #1e1e1e;

    color: #fff;
    text-align: center;
    text-overflow: ellipsis;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 18px; /* 100% */
  }
}

::v-deep(.v-field) {
  border-radius: 12px;
}

::v-deep(.v-field-label) {
  color: #1e1e1e;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: 0.5px;
  opacity: 1;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

@media only screen and (width < $breakpoint-mobile) {
  .action-buttons {
    flex-direction: column;
  }
}

::v-deep(.save-btn.v-btn) {
  min-height: 52px;
  padding: 0 21.111px;
  border-radius: 8px;
  background-color: #f77d1a;
  flex: 1;

  .v-btn__content {
    color: #fff;
    text-align: center;
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 52px; /* 325% */
    text-transform: none;
  }
}

::v-deep(.delete-btn.v-btn) {
  min-height: 52px;
  padding: 0 21.111px;
  border-radius: 8px;
  border-color: #f77d1a;
  flex: 1;

  .v-btn__content {
    color: #f77d1a;
    text-align: center;
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 52px; /* 325% */
    text-transform: none;
  }
}
</style>
